import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { createUniqueId } from '../utils/common-util';
import { API_STATUS } from '../utils/constants';
import { getApiHeaders } from '../utils/header-util';
import { NOTIFICATION_MESSAGES } from '../utils/notification-messages';
import { toastWarn } from '../utils/toasts';

const useSearch = ({ searchQuery, abortSearch, responseKeyName, url }) => {
   const [isLoading, setIsLoading] = useState(false);
   const [responseData, setResponseData] = useState(null);
   const [totalRecordCount, setTotalRecordCount] = useState(0);
   const [searchStatus, setSearchStatus] = useState(API_STATUS.IN_PROGRESS);
   const cancelApiTokenRef = useRef(null);
   const currentRequestRef = useRef(0);

   const cancelSearch = useCallback((showNotification = false) => {
      if (cancelApiTokenRef.current) {
         cancelApiTokenRef.current.abort();
         if (showNotification) {
            toastWarn(NOTIFICATION_MESSAGES.SEARCH_STOPPED);
         }
      }
   }, []);

   const fetchData = useCallback(
      async (
         payload,
         isNewSearch,
         currentRequestId,
         pageToken,
         bqJobInfo,
         prevResponseData
      ) => {
         try {
            const controller = new AbortController();
            cancelApiTokenRef.current = controller;

            const response = await axios.post(
               url,
               { ...payload, pageToken, bqJobInfo: bqJobInfo },
               { signal: controller.signal, headers: getApiHeaders() }
            );

            if (currentRequestId !== currentRequestRef.current) return;

            const newRows = response.data[responseKeyName]?.map((data) => ({
               ...data,
               id: data._id || data.id || createUniqueId(),
            }));

            const updatedRows = isNewSearch
               ? newRows
               : [...prevResponseData, ...newRows];
            setResponseData(newRows);
            setTotalRecordCount((prevCount) => prevCount + newRows.length);
            setIsLoading(false);
            pageToken = response.data.pageToken;
            if (!bqJobInfo) {
               bqJobInfo = response.data.jobId;
            }

            if (!pageToken) {
               const isNoResults = !updatedRows?.length;
               setSearchStatus(
                  isNoResults ? API_STATUS.NO_RESULTS : API_STATUS.COMPLETED
               );
               cancelApiTokenRef.current = null;
               return;
            }
            const newRequestId = currentRequestRef.current + 1;
            currentRequestRef.current = newRequestId;

            fetchData(
               payload,
               false,
               newRequestId,
               pageToken,
               bqJobInfo,
               updatedRows
            );
         } catch (error) {
            console.warn('Error in fetching Fares records', error);
            if (error.name !== 'CanceledError') {
               toastWarn(error.message);
               setIsLoading(false);
               setResponseData([]);
            }
         }
      },
      [responseKeyName, url]
   );

   useEffect(() => {
      if (Object.keys(searchQuery).length > 0) {
         setIsLoading(true);
         cancelSearch();
         setResponseData([]);
         setSearchStatus(API_STATUS.IN_PROGRESS);
         setTotalRecordCount(0);
         const isNewSearch = true;
         fetchData(
            searchQuery,
            isNewSearch,
            currentRequestRef.current,
            null,
            null
         );
      }

      return () => {
         cancelSearch(); // cleanup API calls
      };
   }, [searchQuery, cancelSearch, fetchData]);

   useEffect(() => {
      if (abortSearch && cancelApiTokenRef.current) {
         setIsLoading(false);
         setSearchStatus(API_STATUS.ABORTED);
         const showNotification = true;
         cancelSearch(showNotification);
      }
   }, [abortSearch, cancelSearch]);

   return { isLoading, responseData, searchStatus, totalRecordCount };
};

export default useSearch;
